import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import React, { FC } from 'react';

type IProps = {
    show: boolean;
    handleClose: () => void;
    comment: any;
};

const ViewCommentModal: FC<IProps> = ({ show, handleClose, comment }) => {
    // Helper function to format date
    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    // Calculate word and character count
    const getWordAndCharCount = (text: string) => {
        if (!text) return { words: 0, characters: 0 };
        const trimmedText = text.trim();
        const words = trimmedText.split(/\s+/).filter((word) => word.length > 0).length;
        const characters = text.length;
        return { words, characters };
    };

    const { words, characters } = getWordAndCharCount(comment?.comment || '');

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[600px] md:max-h-[80vh]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">View Comment - #{comment?.id}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                <div className="flex flex-col gap-4">
                    {/* Comment Details */}
                    <div className="border rounded-lg p-4 bg-slate-50">
                        <h3 className="font-bold text-lg mb-4 text-[#bd902e]">Comment Details</h3>
                        <div className="grid grid-cols-1 gap-3">
                            <div>
                                <span className="text-sm font-semibold text-gray-700">Email:</span>
                                <p className="text-gray-900">{comment?.email || comment?.user?.email || '-'}</p>
                            </div>
                            <div>
                                <span className="text-sm font-semibold text-gray-700">Rate:</span>
                                <p className="text-gray-900">{comment?.rate || '-'} / 5</p>
                            </div>
                            <div>
                                <span className="text-sm font-semibold text-gray-700">Visibility:</span>
                                <p className="text-gray-900">
                                    <span
                                        className={`px-2 py-1 rounded text-xs font-semibold ${
                                            comment?.isVisible || comment?.visible
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                        }`}
                                    >
                                        {comment?.isVisible || comment?.visible ? 'Visible' : 'Hidden'}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Comment Text */}
                    <div className="border rounded-lg p-4 bg-slate-50">
                        <h3 className="font-bold text-lg mb-4 text-[#bd902e]">Comment Text</h3>
                        <div className="bg-white p-4 rounded border border-gray-200 max-h-[300px] overflow-y-auto">
                            <p className="text-gray-900 whitespace-pre-wrap break-words">{comment?.comment || '-'}</p>
                        </div>
                        <div className="mt-2 flex justify-end">
                            <span className="text-xs text-gray-500">
                                {words} words / {characters} characters
                            </span>
                        </div>
                    </div>

                    {/* Lifecycle Information */}
                    {(comment?.createdAt || comment?.updatedAt) && (
                        <div className="border rounded-lg p-4 bg-slate-50">
                            <h3 className="font-bold text-lg mb-4 text-[#bd902e]">Lifecycle Information</h3>
                            <div className="grid grid-cols-1 gap-3">
                                {comment?.createdAt && (
                                    <div>
                                        <span className="text-sm font-semibold text-gray-700">Created At:</span>
                                        <p className="text-gray-900">{formatDate(comment.createdAt)}</p>
                                    </div>
                                )}
                                {comment?.updatedAt && (
                                    <div>
                                        <span className="text-sm font-semibold text-gray-700">Updated At:</span>
                                        <p className="text-gray-900">{formatDate(comment.updatedAt)}</p>
                                    </div>
                                )}
                                {comment?.createdBy && (
                                    <div>
                                        <span className="text-sm font-semibold text-gray-700">Created By:</span>
                                        <p className="text-gray-900">{comment.createdBy}</p>
                                    </div>
                                )}
                                {comment?.updatedBy && (
                                    <div>
                                        <span className="text-sm font-semibold text-gray-700">Updated By:</span>
                                        <p className="text-gray-900">{comment.updatedBy}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-center gap-2 mt-5">
                <ButtonBase
                    innerText="Close"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={handleClose}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default ViewCommentModal;

